/**
 * C31 — the FM Manager's "⚠ Emergency" (FM Manager rebuild W6, B23E).
 *
 * The header button is ALWAYS enabled — never greyed out. The pop-up asks
 * "Which site?" (the caller's sites; the default building preselected when it
 * is one of them) and "What?". Without a site, a kind answers "Pick the site
 * first". Each kind maps onto the existing POST /emergency/alerts:
 *
 *   Fire / smoke, Gas leak ............ evacuation (critical) — everyone on site
 *   Lift entrapment, Medical,
 *   Power failure, Flooding ........... incident (warning) — the site team,
 *                                       reception and the L1–L2 escalation list
 *
 * The message is "{Kind}: {detail}" — the FM may write the detail, else a
 * plain default. It asks before sending ("This alerts … Send?") — that step is
 * the route's `confirmation: "CONFIRM"`. A 409 "This will end the live … —
 * continue?" is asked in the route's own words and, on yes, sent again naming
 * the live alert it replaces (`replace_alert_id`). Any other refusal is shown
 * in the route's words.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiGet, apiPostEnvelope } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { useFmShell } from '@/hooks/useFmShell';
import { SiteChips } from '@/components/Emergency/WhichSite';
import { FmSheet, OFFLINE, httpStatusOf, useOnline } from './ticketParts';
import { INPUT, InlineError, refusal } from './permitParts';

export type EmergencyKind = 'Fire / smoke' | 'Lift entrapment' | 'Medical' | 'Power failure' | 'Gas leak' | 'Flooding';

export const KINDS: EmergencyKind[] = ['Fire / smoke', 'Lift entrapment', 'Medical', 'Power failure', 'Gas leak', 'Flooding'];

const DEFAULT_DETAIL: Record<EmergencyKind, string> = {
  'Fire / smoke': 'leave by the nearest exit and go to the assembly point. Do not use the lifts.',
  'Gas leak': 'leave by the nearest exit and go to the assembly point. Do not use switches or lifts.',
  'Lift entrapment': 'someone is trapped in a lift.',
  Medical: 'someone needs medical help.',
  'Power failure': 'the site has lost power.',
  Flooding: 'water is coming in.',
};

/** The alert a kind sends (B23E). */
export function alertFor(kind: EmergencyKind, detail: string): {
  alert_type: 'evacuation' | 'incident'; severity: 'critical' | 'warning'; message: string;
} {
  const evac = kind === 'Fire / smoke' || kind === 'Gas leak';
  return {
    alert_type: evac ? 'evacuation' : 'incident',
    severity: evac ? 'critical' : 'warning',
    message: `${kind}: ${detail.trim() || DEFAULT_DETAIL[kind]}`,
  };
}

export function whoIsTold(kind: EmergencyKind, site: string): string {
  return alertFor(kind, '').alert_type === 'evacuation'
    ? `everyone on site at ${site}`
    : `the site team, security and the escalation list for ${site}`;
}

const REPLACE_ASK = /^This will end the live .+ — continue\?$/;
const PRIMARY = ['evacuation', 'lockdown', 'drill'];

interface Live { id: string; building_id: string; alert_type: string }

export default function EmergencyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const shell = useFmShell();
  const sites = shell.data?.sites ?? [];
  const qc = useQueryClient();
  const online = useOnline();
  const def = user?.default_building_id && sites.some((s) => s.id === user.default_building_id)
    ? user.default_building_id : null;
  const [site, setSite] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const picked = touched ? site : (site ?? def);
  const [kind, setKind] = useState<EmergencyKind | null>(null);
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [replace, setReplace] = useState<{ ask: string; id: string } | null>(null);
  const siteName = sites.find((s) => s.id === picked)?.name ?? '';

  const reset = () => {
    setSite(null); setTouched(false); setKind(null); setDetail(''); setError(null); setReplace(null);
  };
  const close = () => { reset(); onClose(); };

  const send = useMutation({
    mutationFn: (replaceId: string | null) => apiPostEnvelope('/emergency/alerts', {
      ...alertFor(kind!, detail), building_id: picked, confirmation: 'CONFIRM',
      ...(replaceId ? { replace_alert_id: replaceId } : {}) }),
    onSuccess: (r) => {
      toast.success(r.message || 'Alert sent.');
      void qc.invalidateQueries({ queryKey: ['emergency'] });
      close();
    },
    onError: async (e) => {
      void qc.invalidateQueries({ queryKey: ['emergency'] });
      const msg = refusal(e, "Couldn't send the alert. Try again — or call the emergency services.");
      if (httpStatusOf(e) === 409 && REPLACE_ASK.test(msg)) {
        try {
          const live = (await apiGet<Live[]>('/emergency/alerts/active')) ?? [];
          const old = live.find((a) => a.building_id === picked && PRIMARY.includes(a.alert_type));
          if (old) { setError(null); setReplace({ ask: msg, id: old.id }); return; }
        } catch { /* fall through to the words */ }
      }
      setError(msg);
    },
  });

  const choose = (k: EmergencyKind) => {
    if (!picked) {
      toast.error('Pick the site first');
      return;
    }
    setKind(k);
    setError(null);
  };

  const sendWhy = !online ? OFFLINE : send.isPending ? 'Sending…' : null;

  return (
    <FmSheet open={open} onClose={close} title="⚠ Emergency alert" testid="emergency-modal"
             subtitle="Sends an alert to the site team, security and the escalation list.">
      {!kind ? (
        <>
          <div>
            <span className="mb-1 block text-sm font-medium text-content">Which site?</span>
            <SiteChips sites={sites} value={picked}
                       onPick={(id) => { setSite(id); setTouched(true); }} />
          </div>
          <div>
            <span className="mb-1 block text-sm font-medium text-content">What?</span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="emergency-kinds">
              {KINDS.map((k) => (
                <button key={k} type="button" data-testid="emergency-kind" onClick={() => choose(k)}
                        className="min-h-[48px] rounded-lg border border-danger-200 bg-danger-50 px-3 text-sm font-semibold text-danger-700 hover:bg-danger-100">
                  {k}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-content-muted">Never greyed out: if you have no default building, it asks which site.</p>
        </>
      ) : replace ? (
        <div className="space-y-3" data-testid="emergency-replace">
          <p className="text-sm font-semibold text-content">{replace.ask}</p>
          <p className="text-sm text-content-secondary">
            The live alert ends and everyone it reached is told it has been replaced.
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className="btn-secondary min-h-[44px]" onClick={() => setReplace(null)}>Back</button>
            <button type="button" className="btn-danger min-h-[44px]" data-testid="emergency-replace-yes"
                    disabled={!!sendWhy} title={sendWhy ?? undefined} onClick={() => send.mutate(replace.id)}>
              Continue
            </button>
          </div>
        </div>
      ) : (
        <form className="space-y-3" data-testid="emergency-confirm" noValidate
              onSubmit={(e) => { e.preventDefault(); if (!sendWhy) send.mutate(null); }}>
          <p className="text-base font-semibold text-content" data-testid="emergency-what">{kind} at {siteName}</p>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-content-tertiary">Add a detail (optional)</span>
            <input className={INPUT} value={detail} maxLength={900} data-testid="emergency-detail"
                   placeholder={DEFAULT_DETAIL[kind]} onChange={(e) => setDetail(e.target.value)} />
          </label>
          <p className="rounded-lg bg-surface-subtle px-3 py-2 text-sm text-content" data-testid="emergency-ask">
            This alerts {whoIsTold(kind, siteName)}. Send?
          </p>
          <InlineError text={error} testid="emergency-error" />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {sendWhy === OFFLINE && <span className="mr-auto text-xs text-content-muted">{OFFLINE}</span>}
            <button type="button" className="btn-secondary min-h-[44px]"
                    onClick={() => { setKind(null); setError(null); }}>
              Back
            </button>
            <button type="submit" className="btn-danger min-h-[44px]" data-testid="emergency-send"
                    disabled={!!sendWhy} title={sendWhy ?? undefined}>
              Send alert
            </button>
          </div>
        </form>
      )}
    </FmSheet>
  );
}

/** The header button (C0.2). Always enabled. */
export function FmEmergencyButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid="fm-emergency" aria-label="Emergency" onClick={() => setOpen(true)}
              className="inline-flex min-h-[32px] shrink-0 items-center gap-1 whitespace-nowrap rounded-lg bg-danger-600 px-2 py-1 text-xs font-semibold text-white hover:bg-danger-700 sm:px-2.5">
        <span aria-hidden="true">⚠</span><span className="hidden sm:inline">Emergency</span>
      </button>
      <EmergencyModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
